import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import { supabase } from '../services/supabase';
import { AudioManager } from '../services/AudioManager';
import { mountMusicToggleButton, unmountMusicToggleButton } from '../ui/MusicToggleButton';

export class LobbyScene extends Phaser.Scene {
  private nickname: string = '';
  private formElement?: HTMLDivElement;
  private resultElement?: HTMLDivElement;
  private fullscreenPopup?: HTMLDivElement;
  private audioManager?: AudioManager;
  private isMobile: boolean = false;
  private resizeHandler?: () => void;
  private phaserElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'LobbyScene' });
  }

  create() {
    // 기존 요소 정리 (씬 재시작 시)
    this.cleanupForm();
    this.cleanupResult();
    this.cleanupPopup();
    this.cleanupPhaserElements();

    this.isMobile = (window as any).isMobileDevice || false;
    this.audioManager = AudioManager.getInstance(this);
    this.audioManager.startBgm();
    this.mountMusicButton();

    // 화면 방향 전환 이벤트 리스너 설정
    this.resizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);

    // UI 생성
    this.createUI();
  }

  /**
   * 화면 방향/크기 변경 시 UI 재생성
   */
  private handleResize(): void {
    // 팝업이 열려있으면 무시 (폼 재생성 안함)
    if (this.fullscreenPopup) return;

    // 모바일 키보드가 열릴 때 발생하는 resize만 무시한다.
    // 데스크톱 브라우저 리사이즈는 입력 포커스 중이어도 배경/레이아웃을 다시 맞춰야 한다.
    const activeElement = document.activeElement;
    const inputFocused = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA');
    if (this.isMobile && inputFocused) {
      return;
    }

    // 잠시 후 UI 재생성 (화면 크기 안정화 대기)
    setTimeout(() => {
      // 다시 한번 확인 (timeout 중에 포커스 됐을 수 있음)
      const currentActive = document.activeElement;
      if (this.isMobile && currentActive && (currentActive.tagName === 'INPUT' || currentActive.tagName === 'TEXTAREA')) {
        return;
      }

      this.cleanupForm();
      this.cleanupResult();
      this.cleanupPhaserElements();
      this.createUI();
      this.mountMusicButton();
    }, 100);
  }

  /**
   * UI 생성 (Phaser 요소 + HTML 폼)
   */
  private createUI(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.createLobbyBackground(width, height);

    // 배포 버전 표시 (좌측 하단 작은 글씨)
    const versionText = this.add.text(
      8,
      height - 8,
      `v${__APP_VERSION__} · ${__BUILD_TIME__}`,
      {
        fontSize: '11px',
        color: '#FFFFFF',
        fontFamily: 'Nunito',
        stroke: '#075B9E',
        strokeThickness: 2,
      }
    ).setOrigin(0, 1).setScrollFactor(0).setDepth(30).setAlpha(0.85);
    this.phaserElements.push(versionText);

    // 타이틀 크기 및 위치 (모바일/가로세로 대응)
    const isLandscape = width > height;
    const isCompact = this.isCompactLayout(width, height);
    const contentX = this.getLobbyContentX(width, height);
    const titleSize = isCompact
      ? Math.max(34, Math.min(44, Math.floor(width * 0.105)))
      : Math.max(58, Math.min(88, Math.floor(width * 0.068)));
    const titleY = isCompact ? (isLandscape ? height * 0.18 : height * 0.19) : height * 0.26;

    const title = this.add.text(width / 2, titleY, 'LANDLOCK', {
      fontSize: `${titleSize}px`,
      color: '#FFF3C4',
      fontFamily: 'Nunito',
      fontStyle: '900',
      stroke: '#075B9E',
      strokeThickness: isCompact ? 7 : 10,
      shadow: {
        offsetX: 0,
        offsetY: isCompact ? 5 : 8,
        color: '#0A68C2',
        blur: 0,
        stroke: true,
        fill: true,
      },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(20);
    title.setX(contentX);
    this.phaserElements.push(title);

    const subtitleY = titleY + (isCompact ? 42 : 66);
    const subtitleLabel = this.add.graphics();
    subtitleLabel.setScrollFactor(0).setDepth(19);
    const labelWidth = isCompact ? 190 : 250;
    const labelHeight = isCompact ? 28 : 34;
    subtitleLabel.fillStyle(0xFFFFFF, 0.62);
    subtitleLabel.fillRoundedRect(contentX - labelWidth / 2, subtitleY - labelHeight / 2, labelWidth, labelHeight, 8);
    subtitleLabel.lineStyle(2, 0xFFFFFF, 0.72);
    subtitleLabel.strokeRoundedRect(contentX - labelWidth / 2, subtitleY - labelHeight / 2, labelWidth, labelHeight, 8);
    this.phaserElements.push(subtitleLabel);

    const subtitle = this.add.text(contentX, subtitleY, 'TERRITORY PARTY', {
      fontSize: isCompact ? '13px' : '17px',
      color: '#0C5FA5',
      fontFamily: 'Nunito',
      fontStyle: '900',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(20);
    this.phaserElements.push(subtitle);

    // 이전 게임 결과 표시
    const lastResult = useGameStore.getState().lastGameResult;
    if (lastResult) {
      this.showGameResult(lastResult, width, height, subtitleY);
    }

    // HTML 폼 생성
    this.createForm(lastResult !== null);
  }

  private mountMusicButton(): void {
    if (this.audioManager) {
      mountMusicToggleButton(this.audioManager, 'lobby');
    }
  }

  private createLobbyBackground(width: number, height: number): void {
    const bg = this.add.image(0, 0, 'lobby-casual-bg');
    const scale = this.getBackgroundCoverScale(width, height, bg.width, bg.height);
    const position = this.getBackgroundCoverPosition(width, height, bg.width * scale, bg.height * scale);
    bg.setScale(scale);
    bg.setPosition(position.x, position.y);
    bg.setScrollFactor(0);
    bg.setDepth(-20);
    this.phaserElements.push(bg);
  }

  private getLobbyContentX(width: number, height: number): number {
    const isLandscape = width > height;
    if (this.isCompactLayout(width, height)) {
      return width / 2;
    }

    return isLandscape ? Math.min(Math.max(width * 0.29, 320), 560) : width / 2;
  }

  private isCompactLayout(width: number, height: number): boolean {
    return this.isMobile || width < 760 || height < 520;
  }

  private getBackgroundCoverScale(width: number, height: number, imageWidth: number, imageHeight: number): number {
    return Math.max(width / imageWidth, height / imageHeight);
  }

  private getBackgroundCoverPosition(
    width: number,
    height: number,
    scaledWidth: number,
    scaledHeight: number
  ): { x: number; y: number } {
    const isPortrait = height > width;
    // Portrait mobile crops the wide lobby art heavily, so bias the crop toward
    // the right-side mascot instead of the empty title area.
    const sourceFocusX = isPortrait ? 0.74 : 0.5;
    const sourceFocusY = isPortrait ? 0.5 : 0.5;
    const viewportFocusX = width / 2;
    const viewportFocusY = height / 2;

    const minLeft = Math.min(0, width - scaledWidth);
    const minTop = Math.min(0, height - scaledHeight);
    const left = Phaser.Math.Clamp(viewportFocusX - scaledWidth * sourceFocusX, minLeft, 0);
    const top = Phaser.Math.Clamp(viewportFocusY - scaledHeight * sourceFocusY, minTop, 0);

    return {
      x: left + scaledWidth / 2,
      y: top + scaledHeight / 2,
    };
  }

  private showGameResult(
    result: {
      score: number;
      kills: number;
      maxTerritoryPercent: number;
      playTime: number;
      deathCause?: string | null;
    },
    width: number,
    height: number,
    subtitleY: number
  ): void {
    const isLandscape = width > height;
    const isCompact = this.isCompactLayout(width, height);
    const hasLimitedHeight = !isCompact && height < 820;
    const hasCause = !!result.deathCause;
    const boxWidth = isCompact
      ? (isLandscape ? 240 : Math.min(280, width - 40))
      : 320;
    // 사망 원인 줄이 있으면 한 줄 분량만큼 키운다
    const boxHeight = isCompact
      ? (hasCause ? 98 : 80)
      : (hasLimitedHeight ? (hasCause ? 102 : 84) : (hasCause ? 120 : 100));

    let resultX = isCompact ? width / 2 : this.getLobbyContentX(width, height);
    let resultY = isCompact
      ? (isLandscape ? subtitleY + 50 : subtitleY + 60)
      : this.getDesktopResultY(height, subtitleY, boxHeight, hasLimitedHeight);

    if (isCompact && isLandscape) {
      // 가로 모바일: 세로 공간이 부족해 결과 패널이 폼(z-index 100) 뒤에 깔렸다.
      // 좌우 공간은 넉넉하므로 폼 왼쪽에 나란히 배치하고 폼 중심 높이에 맞춘다.
      const formHalfWithGap = 143 + 12; // 컴팩트 폼 절반(~143) + 간격
      resultX = Math.max(boxWidth / 2 + 10, width / 2 - formHalfWithGap - boxWidth / 2);
      resultY = height * 0.72 - boxHeight / 2;
    }

    // 플레이 시간 포맷팅
    const minutes = Math.floor(result.playTime / 60);
    const seconds = result.playTime % 60;
    const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    const fontSize1 = isCompact ? '16px' : (hasLimitedHeight ? '18px' : '20px');
    const fontSize2 = isCompact ? '12px' : (hasLimitedHeight ? '15px' : '16px');
    const fontSize3 = isCompact ? '10px' : (hasLimitedHeight ? '13px' : '14px');
    const titleOffset = isCompact ? 18 : (hasLimitedHeight ? 21 : 25);
    // 사망 원인 줄이 끼어들면 아래 줄들을 한 칸씩 내린다
    const causeOffset = isCompact ? 37 : (hasLimitedHeight ? 41 : 48);
    const lineShift = hasCause ? (isCompact ? 18 : (hasLimitedHeight ? 19 : 21)) : 0;
    const scoreOffset = (isCompact ? 40 : (hasLimitedHeight ? 43 : 50)) + lineShift;
    const statsOffset = (isCompact ? 58 : (hasLimitedHeight ? 64 : 75)) + lineShift;

    this.resultElement = document.createElement('div');
    this.resultElement.id = 'game-result-panel';
    this.resultElement.style.cssText = `
      position: fixed;
      top: ${resultY}px;
      left: ${resultX}px;
      transform: translateX(-50%);
      width: ${boxWidth}px;
      height: ${boxHeight}px;
      background: rgba(44, 62, 80, 0.92);
      border-radius: 8px;
      box-shadow: 0 8px 0 rgba(7, 91, 158, 0.18), 0 16px 32px rgba(0, 33, 66, 0.18);
      color: #FFFFFF;
      display: block;
      text-align: center;
      font-family: Nunito, sans-serif;
      z-index: 95;
      pointer-events: none;
      -webkit-font-smoothing: antialiased;
      text-rendering: geometricPrecision;
    `;

    this.resultElement.innerHTML = `
      <div style="
        position: absolute;
        top: ${titleOffset}px;
        left: 0;
        width: 100%;
        transform: translateY(-50%);
        color: #FF6B6B;
        font-size: ${fontSize1};
        font-weight: 900;
        line-height: 1;
      ">GAME OVER</div>
      <div id="result-death-cause" style="
        position: absolute;
        top: ${causeOffset}px;
        left: 0;
        width: 100%;
        transform: translateY(-50%);
        color: #FFD166;
        font-size: ${fontSize3};
        font-weight: 800;
        line-height: 1;
        display: ${hasCause ? 'block' : 'none'};
      "></div>
      <div style="
        position: absolute;
        top: ${scoreOffset}px;
        left: 0;
        width: 100%;
        transform: translateY(-50%);
        color: #FFFFFF;
        font-size: ${fontSize2};
        font-weight: 700;
        line-height: 1;
      ">Score: ${result.score}  |  Kills: ${result.kills}</div>
      <div style="
        position: absolute;
        top: ${statsOffset}px;
        left: 0;
        width: 100%;
        transform: translateY(-50%);
        color: #D3DEE8;
        font-size: ${fontSize3};
        font-weight: 700;
        line-height: 1;
      ">Max: ${result.maxTerritoryPercent.toFixed(1)}%  |  Time: ${timeStr}</div>
    `;

    document.body.appendChild(this.resultElement);

    // 사망 원인에는 상대 닉네임(사용자 입력)이 포함되므로
    // innerHTML이 아닌 textContent로 주입한다 (XSS 방지)
    if (hasCause) {
      const causeElement = this.resultElement.querySelector('#result-death-cause');
      if (causeElement) {
        causeElement.textContent = `💀 ${result.deathCause}`;
      }
    }
  }

  private getDesktopResultY(
    height: number,
    subtitleY: number,
    boxHeight: number,
    hasLimitedHeight: boolean
  ): number {
    const naturalY = height / 2 - 30;
    const formCenterY = height * 0.69;
    const estimatedFormHeight = hasLimitedHeight ? 216 : 232;
    const formTopY = formCenterY - estimatedFormHeight / 2;
    const gap = hasLimitedHeight ? 10 : 22;
    const maxY = formTopY - boxHeight - gap;
    const minY = subtitleY + (hasLimitedHeight ? 40 : 54);

    if (maxY < minY) {
      return Math.max(subtitleY + 34, maxY);
    }

    return Math.min(Math.max(naturalY, minY), maxY);
  }

  private createForm(hasResult: boolean): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isLandscape = width > height;
    const isCompact = this.isCompactLayout(width, height);
    const contentX = this.getLobbyContentX(width, height);

    // 폼 위치 계산
    let topPosition: string;
    if (isCompact) {
      topPosition = hasResult
        ? (isLandscape ? '72%' : '78%')
        : (isLandscape ? '64%' : '82%');
    } else {
      topPosition = hasResult ? '69%' : '56%';
    }

    const inputWidth = isCompact ? `${Math.max(170, Math.min(208, width - 118))}px` : '230px';
    const fontSize = isCompact ? '14px' : '18px';
    const btnFontSize = isCompact ? '16px' : '22px';
    const padding = isCompact ? '17px 18px' : '24px 30px';
    const panelWidth = isCompact ? `${Math.max(248, Math.min(286, width - 48))}px` : '320px';

    this.formElement = document.createElement('div');
    this.formElement.id = 'lobby-form';
    this.formElement.style.cssText = `
      position: fixed;
      top: ${topPosition};
      left: ${contentX}px;
      transform: translate(-50%, -50%);
      text-align: center;
      width: ${panelWidth};
      background: rgba(255, 255, 255, 0.72);
      padding: ${padding};
      border: 2px solid rgba(255, 255, 255, 0.82);
      border-radius: 8px;
      box-shadow: 0 14px 0 rgba(7, 91, 158, 0.22), 0 22px 44px rgba(0, 82, 160, 0.18);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      z-index: 100;
    `;

    this.formElement.innerHTML = `
      <div style="
        color: #075B9E;
        font-size: ${isCompact ? '12px' : '13px'};
        font-family: Nunito, sans-serif;
        font-weight: 900;
        letter-spacing: 0;
        margin-bottom: ${isCompact ? '10px' : '12px'};
      ">
        READY TO CLAIM
      </div>
      <div style="margin-bottom: ${isCompact ? '12px' : '16px'};">
        <input
          type="text"
          id="nickname-input"
          placeholder="닉네임 입력"
          maxlength="12"
          style="
            padding: ${isCompact ? '10px 15px' : '12px 20px'};
            font-size: ${fontSize};
            border: 3px solid #75D9FF;
            border-radius: 8px;
            width: ${inputWidth};
            outline: none;
            background: #FFFDF5;
            color: #075B9E;
            text-align: center;
            font-weight: 800;
            transition: border-color 0.2s, box-shadow 0.2s;
            font-family: Nunito, sans-serif;
            box-shadow: inset 0 3px 0 rgba(7, 91, 158, 0.08);
          "
        >
      </div>
      <button
        id="start-btn"
        style="
          padding: ${isCompact ? '12px 44px' : '14px 58px'};
          font-size: ${btnFontSize};
          background: linear-gradient(180deg, #FFE74E 0%, #FFB72C 100%);
          color: #13518B;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-family: Nunito, sans-serif;
          font-weight: 900;
          transition: transform 0.1s, box-shadow 0.2s;
          box-shadow: 0 7px 0 #E8791A, 0 14px 26px rgba(232, 121, 26, 0.28);
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.55);
        "
      >
        START
      </button>
      <div style="
        display: flex;
        justify-content: center;
        gap: 7px;
        margin-top: ${isCompact ? '14px' : '16px'};
      ">
        <span style="width: 8px; height: 8px; border-radius: 50%; background: #FF7EB6; display: block;"></span>
        <span style="width: 8px; height: 8px; border-radius: 50%; background: #26D9D0; display: block;"></span>
        <span style="width: 8px; height: 8px; border-radius: 50%; background: #FFE74E; display: block;"></span>
      </div>
    `;

    document.body.appendChild(this.formElement);

    const input = document.getElementById('nickname-input') as HTMLInputElement;
    const startBtn = document.getElementById('start-btn') as HTMLButtonElement;

    // 마지막으로 사용한 닉네임 자동 입력
    // (innerHTML이 아니라 DOM 프로퍼티로 넣어 XSS 우려 없음)
    try {
      const savedNickname = localStorage.getItem('landlock_nickname');
      if (savedNickname && !input.value) {
        input.value = savedNickname.slice(0, 12);
      }
    } catch {
      // localStorage 접근 불가 환경(시크릿 모드 일부 등)에서는 무시
    }

    // 입력 필드 포커스 효과
    input.addEventListener('focus', () => {
      input.style.borderColor = '#FF7EB6';
      input.style.boxShadow = '0 0 0 4px rgba(255, 126, 182, 0.22), inset 0 3px 0 rgba(7, 91, 158, 0.08)';
    });
    input.addEventListener('blur', () => {
      input.style.borderColor = '#75D9FF';
      input.style.boxShadow = 'inset 0 3px 0 rgba(7, 91, 158, 0.08)';
    });

    // 버튼 호버 효과
    startBtn.addEventListener('mouseenter', () => {
      startBtn.style.transform = 'scale(1.05)';
      startBtn.style.boxShadow = '0 9px 0 #E8791A, 0 18px 32px rgba(232, 121, 26, 0.35)';
    });
    startBtn.addEventListener('mouseleave', () => {
      startBtn.style.transform = 'scale(1)';
      startBtn.style.boxShadow = '0 7px 0 #E8791A, 0 14px 26px rgba(232, 121, 26, 0.28)';
    });

    // 엔터키로 시작
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleStartClick();
      }
    });

    // 버튼 클릭으로 시작
    startBtn.addEventListener('click', () => {
      this.handleStartClick();
    });

    // 입력 필드에 포커스 (모바일 아닐 때만)
    if (!this.isMobile) {
      input.focus();
    }
  }

  /**
   * START 버튼 클릭 처리
   */
  private handleStartClick(): void {
    this.audioManager?.play('button_click');
    const input = document.getElementById('nickname-input') as HTMLInputElement;
    const typedNickname = input.value.trim();
    this.nickname = typedNickname || `Player_${Math.floor(Math.random() * 1000)}`;

    // 직접 입력한 닉네임만 기억한다 (랜덤 폴백 이름은 저장하지 않음)
    if (typedNickname) {
      try {
        localStorage.setItem('landlock_nickname', typedNickname);
      } catch {
        // localStorage 접근 불가 환경에서는 무시
      }
    }

    if (this.isMobile) {
      // 홈 화면에 추가된 앱 모드(standalone)는 이미 전체화면이므로 바로 시작
      if ((navigator as any).standalone === true) {
        this.startGame();
        return;
      }
      // 모바일: 전체화면 안내 팝업 표시
      this.showFullscreenPopup();
    } else {
      // 데스크톱: 바로 게임 시작
      this.startGame();
    }
  }

  /**
   * Fullscreen API 지원 여부.
   * iPhone Safari는 비디오 외 요소의 전체화면을 지원하지 않아 항상 false다
   * (코드 문제가 아니라 iOS 플랫폼 제약 - 홈 화면에 추가하면 앱 모드로 전체화면 가능).
   */
  private isFullscreenSupported(): boolean {
    const docEl = document.documentElement as any;
    return !!(
      docEl.requestFullscreen ||
      docEl.webkitRequestFullscreen ||
      docEl.mozRequestFullScreen ||
      docEl.msRequestFullscreen
    );
  }

  /**
   * 모바일 전체화면 안내 팝업
   */
  private showFullscreenPopup(): void {
    // 기존 폼 숨기기
    if (this.formElement) {
      this.formElement.style.display = 'none';
    }

    this.fullscreenPopup = document.createElement('div');
    this.fullscreenPopup.id = 'fullscreen-popup';
    this.fullscreenPopup.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      font-family: Nunito, sans-serif;
    `;

    const fullscreenSupported = this.isFullscreenSupported();

    // iPhone Safari는 Fullscreen API 미지원 → 동작하지 않는 버튼 대신
    // "홈 화면에 추가" 안내를 보여준다 (추가하면 앱 모드 = 전체화면으로 실행됨)
    const actionSection = fullscreenSupported
      ? `
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="fullscreen-btn" style="
            padding: 14px 35px;
            font-size: 16px;
            background: linear-gradient(135deg, #4D96FF, #6BA3FF);
            color: white;
            border: none;
            border-radius: 10px;
            cursor: pointer;
            font-weight: bold;
            box-shadow: 0 4px 15px rgba(77, 150, 255, 0.5);
          ">
            🖥️ 전체화면으로 시작
          </button>
          <button id="normal-btn" style="
            padding: 10px 35px;
            font-size: 13px;
            background: transparent;
            color: #95A5A6;
            border: 1px solid #95A5A6;
            border-radius: 10px;
            cursor: pointer;
          ">
            그냥 시작하기
          </button>
        </div>`
      : `
        <p style="color: #B8C4CE; font-size: 12px; line-height: 1.6; margin-bottom: 10px;
                  background: rgba(255,255,255,0.08); border-radius: 10px; padding: 12px;">
          이 브라우저는 전체화면을 지원하지 않습니다.<br>
          Safari <strong style="color:#FFD700;">공유 버튼 → '홈 화면에 추가'</strong>로 설치하면<br>
          주소창 없는 전체화면 앱처럼 즐길 수 있어요!
        </p>
        <p style="color: #95A5A6; font-size: 11px; line-height: 1.6; margin-bottom: 16px;">
          또는 주소창의 <strong style="color:#B8C4CE;">'Aa' 메뉴 → '도구 막대 가리기'</strong>로<br>
          주소창을 잠시 숨길 수 있어요.
        </p>
        <button id="normal-btn" style="
          padding: 14px 35px;
          font-size: 16px;
          background: linear-gradient(135deg, #4D96FF, #6BA3FF);
          color: white;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          font-weight: bold;
          box-shadow: 0 4px 15px rgba(77, 150, 255, 0.5);
        ">
          시작하기
        </button>`;

    this.fullscreenPopup.innerHTML = `
      <div style="text-align: center; padding: 20px; max-width: 300px;">
        <div style="font-size: 50px; margin-bottom: 15px;">📱🔄</div>
        <h2 style="color: #4D96FF; font-size: 18px; margin-bottom: 12px;">
          최적의 게임 환경
        </h2>
        <p style="color: #FFFFFF; font-size: 14px; line-height: 1.5; margin-bottom: 20px;">
          더 넓은 화면으로 게임을 즐기려면<br>
          <strong style="color: #FFD700;">가로 모드</strong>를 권장합니다!
        </p>
        ${actionSection}
        <p style="color: #666; font-size: 11px; margin-top: 15px;">
          💡 기기를 가로로 돌려주세요
        </p>
      </div>
    `;

    document.body.appendChild(this.fullscreenPopup);

    // 전체화면 버튼
    const fullscreenBtn = document.getElementById('fullscreen-btn');
    fullscreenBtn?.addEventListener('click', () => {
      this.audioManager?.play('button_click');
      this.requestFullscreenAndStart();
    });

    // 일반 시작 버튼
    const normalBtn = document.getElementById('normal-btn');
    normalBtn?.addEventListener('click', () => {
      this.audioManager?.play('button_click');
      this.cleanupPopup();
      this.startGame();
    });
  }

  /**
   * 전체화면 및 가로모드 요청 후 게임 시작
   */
  private async requestFullscreenAndStart(): Promise<void> {
    try {
      // 전체화면 요청
      const docEl = document.documentElement;
      if (docEl.requestFullscreen) {
        await docEl.requestFullscreen();
      } else if ((docEl as any).webkitRequestFullscreen) {
        await (docEl as any).webkitRequestFullscreen();
      } else if ((docEl as any).mozRequestFullScreen) {
        await (docEl as any).mozRequestFullScreen();
      } else if ((docEl as any).msRequestFullscreen) {
        await (docEl as any).msRequestFullscreen();
      }

      // 화면 방향 고정 시도 (가로 모드)
      try {
        if (screen.orientation && (screen.orientation as any).lock) {
          await (screen.orientation as any).lock('landscape');
        }
      } catch (orientationError) {
        // 방향 고정 실패해도 계속 진행
        console.log('Screen orientation lock not supported');
      }
    } catch (error) {
      console.log('Fullscreen request failed:', error);
    }

    // 팝업 정리 후 게임 시작
    this.cleanupPopup();

    // 약간의 딜레이 후 게임 시작 (화면 전환 대기)
    setTimeout(() => {
      this.startGame();
    }, 300);
  }

  private async startGame(): Promise<void> {
    // 이벤트 리스너 제거
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
    }

    // 폼 제거
    this.cleanupForm();
    this.cleanupResult();

    // 로딩 표시
    this.showLoadingIndicator();

    try {
      // 고유 사용자 ID 생성 (세션 기반)
      let userId = sessionStorage.getItem('landlock_user_id');
      if (!userId) {
        userId = crypto.randomUUID();
        sessionStorage.setItem('landlock_user_id', userId);
      }

      if (this.shouldStartLocalGameWithoutRemoteJoin()) {
        this.audioManager?.stopMusic('lobby_bgm');
        this.startLocalGameTest(userId, 'loopback Supabase URL on hosted page');
        return;
      }

      // room-join Edge Function 호출
      const { data, error } = await this.invokeRoomJoinWithTimeout(userId);

      if (error || !data?.success) {
        console.error('Room join failed:', error || data?.error);
        if (this.startLocalGameTest(userId, 'room-join failed')) {
          return;
        }

        this.hideLoadingIndicator();
        this.showError('서버 연결에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      console.log('Room joined:', data);

      // 로딩 인디케이터 숨기기
      this.hideLoadingIndicator();
      this.audioManager?.stopMusic('lobby_bgm');

      // 게임 시작
      this.scene.start('GameScene', {
        nickname: this.nickname,
        roomId: data.room_id,
        playerId: userId,
        spawnPosition: data.spawn_position,
        color: data.color,
        players: data.players
      });

    } catch (err) {
      console.error('Failed to join room:', err);
      const fallbackUserId = sessionStorage.getItem('landlock_user_id') || 'local-test-player';
      if (this.startLocalGameTest(fallbackUserId, 'room-join exception')) {
        return;
      }

      this.hideLoadingIndicator();
      this.showError('서버 연결에 실패했습니다. 다시 시도해주세요.');
    }
  }

  private invokeRoomJoinWithTimeout(userId: string) {
    const timeoutMs = import.meta.env.VITE_ENABLE_LOCAL_GAME_TEST === 'true' ? 3000 : 10000;
    const request = supabase.functions.invoke('room-join', {
        body: {
          user_id: userId,
          nickname: this.nickname
        }
      });

    return Promise.race([
      request,
      new Promise<never>((_, reject) => {
        window.setTimeout(() => {
          reject(new Error(`room-join timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      }),
    ]);
  }

  private shouldStartLocalGameWithoutRemoteJoin(): boolean {
    if (import.meta.env.VITE_ENABLE_LOCAL_GAME_TEST !== 'true') {
      return false;
    }

    const pageHost = window.location.hostname;
    const isLocalPage = pageHost === 'localhost' || pageHost === '127.0.0.1' || pageHost === '::1';
    if (isLocalPage) {
      return false;
    }

    try {
      const supabaseUrl = new URL(import.meta.env.VITE_SUPABASE_URL || '');
      return supabaseUrl.hostname === 'localhost' || supabaseUrl.hostname === '127.0.0.1' || supabaseUrl.hostname === '::1';
    } catch {
      return false;
    }
  }

  private startLocalGameTest(userId: string, reason: string): boolean {
    if (import.meta.env.VITE_ENABLE_LOCAL_GAME_TEST !== 'true') {
      return false;
    }

    console.warn(`Starting offline local game test mode: ${reason}`);
    this.hideLoadingIndicator();
    this.audioManager?.stopMusic('lobby_bgm');
    this.scene.start('GameScene', {
      nickname: this.nickname || 'Local Test',
      roomId: 'local-test-room',
      playerId: userId,
      spawnPosition: { x: 50, y: 50 },
      color: '#4D96FF',
      players: [],
      offline: true,
    });
    return true;
  }

  private showLoadingIndicator(): void {
    const loading = document.createElement('div');
    loading.id = 'loading-indicator';
    loading.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px 40px;
      border-radius: 10px;
      font-family: Nunito, sans-serif;
      font-size: 16px;
      z-index: 10000;
    `;
    loading.textContent = '서버에 연결 중...';
    document.body.appendChild(loading);
  }

  private hideLoadingIndicator(): void {
    const loading = document.getElementById('loading-indicator');
    if (loading) {
      loading.remove();
    }
  }

  private showError(message: string): void {
    // 에러 표시 후 폼 다시 생성
    const errorDiv = document.createElement('div');
    errorDiv.id = 'error-message';
    errorDiv.style.cssText = `
      position: fixed;
      top: 20%;
      left: 50%;
      transform: translateX(-50%);
      background: #FF6B6B;
      color: white;
      padding: 15px 30px;
      border-radius: 8px;
      font-family: Nunito, sans-serif;
      font-size: 14px;
      z-index: 10000;
    `;
    errorDiv.textContent = message;
    document.body.appendChild(errorDiv);

    // 3초 후 에러 메시지 제거 및 폼 재생성
    setTimeout(() => {
      const error = document.getElementById('error-message');
      if (error) error.remove();
      this.createUI();
    }, 3000);
  }

  private cleanupForm(): void {
    // ID로 기존 폼 요소 찾아서 제거
    const existingForm = document.getElementById('lobby-form');
    if (existingForm && existingForm.parentNode) {
      existingForm.parentNode.removeChild(existingForm);
    }

    // 현재 참조도 정리
    if (this.formElement && this.formElement.parentNode) {
      this.formElement.parentNode.removeChild(this.formElement);
    }
    this.formElement = undefined;
  }

  private cleanupResult(): void {
    const existingResult = document.getElementById('game-result-panel');
    if (existingResult && existingResult.parentNode) {
      existingResult.parentNode.removeChild(existingResult);
    }

    if (this.resultElement && this.resultElement.parentNode) {
      this.resultElement.parentNode.removeChild(this.resultElement);
    }
    this.resultElement = undefined;
  }

  private cleanupPopup(): void {
    const existingPopup = document.getElementById('fullscreen-popup');
    if (existingPopup && existingPopup.parentNode) {
      existingPopup.parentNode.removeChild(existingPopup);
    }

    if (this.fullscreenPopup && this.fullscreenPopup.parentNode) {
      this.fullscreenPopup.parentNode.removeChild(this.fullscreenPopup);
    }
    this.fullscreenPopup = undefined;
  }

  private cleanupPhaserElements(): void {
    for (const element of this.phaserElements) {
      if (element && !element.scene) continue;
      element.destroy();
    }
    this.phaserElements = [];
  }

  shutdown() {
    // 이벤트 리스너 제거
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
    }

    this.cleanupForm();
    this.cleanupResult();
    this.cleanupPopup();
    this.cleanupPhaserElements();
    this.audioManager?.stopMusic('lobby_bgm');
    unmountMusicToggleButton();
  }
}
